import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ContractViewer.css';

function ContractViewer({ listingId, transactionId, listingType, userType }) {
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, transactionId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch templates based on listing type
      const type = listingType === 'seller_finance' ? 'seller_finance' : 'standard';
      const templatesRes = await axios.get(`/api/contracts/templates/type/${type}`, { headers });
      setTemplates(templatesRes.data);

      // Fetch existing contracts
      const endpoint = transactionId 
        ? `/api/contracts/transaction/${transactionId}`
        : `/api/contracts/listing/${listingId}`;
      const contractsRes = await axios.get(endpoint, { headers });
      setContracts(contractsRes.data);
    } catch (error) {
      console.error('Error fetching contract data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateContract = async (template) => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/contracts/generate', {
        listing_id: listingId,
        transaction_id: transactionId,
        template_id: template.id,
        contract_type: template.type,
        filled_data: {}
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      fetchData();
      setActiveTab('contracts');
    } catch (error) {
      alert('Error generating contract');
    } finally {
      setGenerating(false);
    }
  };

  const submitForReview = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/contracts/${contractId}/submit-for-review`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error submitting contract');
    }
  };

  const signContract = async (contractId, role) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/contracts/${contractId}/${role}-sign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error signing contract');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'badge-gray', text: 'Draft' },
      pending_review: { class: 'badge-yellow', text: 'Pending Review' },
      approved: { class: 'badge-green', text: 'Approved' },
      rejected: { class: 'badge-red', text: 'Rejected' },
      signed: { class: 'badge-blue', text: 'Partially Signed' },
      executed: { class: 'badge-purple', text: 'Executed' }
    };
    return badges[status] || { class: 'badge-gray', text: status };
  };

  if (loading) return <div className="loading">Loading contracts...</div>;

  return (
    <div className="contract-viewer">
      <div className="contract-header">
        <h2>📄 Texas Real Estate Contracts</h2>
        <p>Attorney-drafted contracts compliant with Texas Real Estate Commission (TREC) requirements</p>
      </div>

      <div className="contract-tabs">
        <button 
          className={`contract-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Available Templates
        </button>
        <button 
          className={`contract-tab ${activeTab === 'contracts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          My Contracts ({contracts.length})
        </button>
      </div>

      {activeTab === 'templates' && (
        <div className="templates-section">
          <div className="contract-type-header">
            <h3>{listingType === 'seller_finance' ? '🏦 Seller Finance' : '🏠 Traditional'} Contract Package</h3>
          </div>

          <div className="templates-grid">
            {templates.map(template => (
              <div key={template.id} className="template-card">
                <div className="template-icon">
                  {template.type.includes('purchase') ? '📋' : 
                   template.type.includes('addendum') ? '📎' : '📝'}
                </div>
                <h4>{template.name}</h4>
                <p>{template.description}</p>
                <div className="template-meta">
                  <span className="state-badge">Texas</span>
                  <span className="version">v{template.version || '1.0'}</span>
                </div>
                {userType === 'seller' && (
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => generateContract(template)}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate Contract'}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="info-box">
            <h4>ℹ️ About These Contracts</h4>
            <p>
              All contracts are based on Texas Real Estate Commission (TREC) approved forms and have been 
              reviewed by licensed Texas attorneys. They include all required disclosures and addenda 
              for your transaction type.
            </p>
            <ul>
              <li>Compliant with Texas Property Code</li>
              <li>Updated to latest TREC versions</li>
              <li>Includes all mandatory disclosures</li>
              <li>Attorney review included in flat fee</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'contracts' && (
        <div className="contracts-section">
          {contracts.length === 0 ? (
            <div className="empty-state">
              <p>No contracts generated yet.</p>
              <button className="btn btn-primary" onClick={() => setActiveTab('templates')}>
                View Available Templates
              </button>
            </div>
          ) : (
            <div className="contracts-list">
              {contracts.map(contract => {
                const badge = getStatusBadge(contract.status);
                return (
                  <div key={contract.id} className="contract-card">
                    <div className="contract-info">
                      <h4>{contract.template_name}</h4>
                      <p>{contract.template_description}</p>
                      <div className="contract-meta">
                        <span className={`status-badge ${badge.class}`}>{badge.text}</span>
                        <span className="date">Created: {new Date(contract.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="contract-signatures">
                      <div className={`signature ${contract.seller_signed ? 'signed' : ''}`}>
                        {contract.seller_signed ? '✅' : '⬜'} Seller
                      </div>
                      <div className={`signature ${contract.buyer_signed ? 'signed' : ''}`}>
                        {contract.buyer_signed ? '✅' : '⬜'} Buyer
                      </div>
                    </div>

                    <div className="contract-actions">
                      {contract.status === 'draft' && userType === 'seller' && (
                        <button className="btn btn-primary btn-sm" onClick={() => submitForReview(contract.id)}>
                          Submit for Review
                        </button>
                      )}
                      {contract.status === 'approved' && userType === 'seller' && !contract.seller_signed && (
                        <button className="btn btn-success btn-sm" onClick={() => signContract(contract.id, 'seller')}>
                          Sign as Seller
                        </button>
                      )}
                      {contract.status === 'approved' && userType === 'buyer' && !contract.buyer_signed && (
                        <button className="btn btn-success btn-sm" onClick={() => signContract(contract.id, 'buyer')}>
                          Sign as Buyer
                        </button>
                      )}
                      {contract.status === 'signed' && (
                        <span className="waiting-text">Waiting for other party...</span>
                      )}
                      {contract.status === 'executed' && (
                        <button className="btn btn-secondary btn-sm">Download PDF</button>
                      )}
                    </div>

                    {contract.attorney_notes && (
                      <div className="attorney-notes">
                        <strong>Attorney Notes:</strong> {contract.attorney_notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContractViewer;
